import { type FC, useCallback, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import NuFeatureGenomeView from "./NuFeatureGenomeView.js";
import NuFeatureSummaryView from "./NuFeatureSummaryView.js";
import { isGenomeViewerEnabledEntity } from "@/app-utils/entity.ts";
import { type INuFeatureState, loadNuFeature } from "@/redux/modules/nuFeature.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE } from "@/settings/settings.js";

const NuFeatureDetailPage: FC = () => {
  // redux
  const dispatch = useDispatch<ReduxDispatch>();
  const { nuFeature, isLoading } = useSelector<IAppState, INuFeatureState>((state) => state.nuFeature, shallowEqual);

  // router
  const { nuFeatureId } = useParams<"nuFeatureId">();

  useEffect(() => {
    void dispatch(loadNuFeature(Number(nuFeatureId)));
  }, [dispatch, nuFeatureId]);

  const getNavItems = useCallback((): IRouteNavItemProps[] => {
    const genomeViewerDisabled = !isGenomeViewerEnabledEntity(nuFeature?.genome);

    const genomeViewDec = {
      disabled: isLoading || genomeViewerDisabled,
      tooltip: genomeViewerDisabled ? "Only features on genomic sequences can be loaded to the Genome Viewer." : null,
    };

    return [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <NuFeatureSummaryView />,
        default: true,
      },
      {
        label: "Genome View",
        route: "genome",
        disabled: genomeViewDec.disabled,
        disabledTooltip: genomeViewDec.tooltip,
        pageComponent: <NuFeatureGenomeView />,
      },
    ];
  }, [isLoading, nuFeature?.id]);

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.NUFEATURE}
      groupSubtypeId={nuFeature?.nuFeatureType.id}
      entityId={Number(nuFeatureId)}
      getDisplayName={() => nuFeature?.name ?? ""}
      sidebarNavItems={getNavItems()}
      isLoading={isLoading}
    />
  );
};

export { NuFeatureDetailPage };
